<template>
  <navbar-menu-tool
    v-model="isMenuOpen"
    :menu-data="{ name, position: 'left', allowOtherTop: true }"
    :button-bind="{ icon, tooltip, verboseName: tooltip }"
    :loading="initLoading"
    has-close-header
    has-footer
  >
    <template #content>
      <div class="ma-2 text-body-2">
        <span class="text-left" style="font-size: 12px">
          <v-row no-gutters class="py-2" align="center">
            <v-col cols="5"> {{ 'Szukaj działek na obszarze' }}: </v-col>
            <v-col cols="7">
              <dots-menu
                :items="dotsItemsArea"
                @multiple="turnOnDrawing('multiple')"
                @freehand="turnOnDrawing('freehand')"
                @single="turnOnDrawing('single')"
              >
                <template #activator="{ on }">
                  <base-button
                    v-on="getButtonListeners(on)"
                    translation-path="sidebar.searchArea.selectArea"
                    :disabled="isDrawn"
                    class="mr-2"
                    :button-style="buttonStyle"
                  />
                </template>
              </dots-menu>
              <v-tooltip v-if="isDrawn || !!selectedFeatures.length" color="font" bottom>
                <template #activator="{ on }">
                  <v-icon v-on="on" color="error" :disabled="isLoading" @click="clearSelection">mdi-cancel</v-icon>
                </template>
                {{ $i18n.t('sidebar.searchArea.clearSelection') }}
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row no-gutters class="py-2" align="center">
            <v-col cols="5"> {{ $i18n.t('sidebar.searchArea.areaFromTo') }}: </v-col>
            <v-col cols="7" class="d-flex">
              <v-row no-gutters align="center">
                <v-col cols="auto" class="d-flex align-center input-area">
                  <data-input v-model="payload['area_from']" v-bind="areaInput" />
                  <span class="ml-1 mr-2 italic--text">ha</span>
                </v-col>
                <v-col cols="auto" class="d-flex align-center input-area">
                  <data-input v-model="payload['area_to']" v-bind="areaInput" />
                  <span class="ml-1 mr-2 italic--text">ha</span>
                </v-col>
                <popover-menu class="pl-1">
                  <template #activator="{ on }">
                    <v-icon size="18" v-on="on" color="primary">mdi-help-circle</v-icon>
                  </template>
                  <v-row no-gutters>
                    <v-col cols="12" style="width: 345px" class="tw:text-left tw:text-xs/5">
                      <v-row no-gutters class="pb-2 italic--text"> {{ $i18n.t('sidebar.searchArea.tooltip1') }} </v-row>
                      <v-row no-gutters class="font-weight-medium"> {{ $i18n.t('sidebar.searchArea.title2') }} </v-row>
                      <v-row no-gutters class="pb-2 italic--text"> {{ $i18n.t('sidebar.searchArea.tooltip2') }} </v-row>
                      <v-row no-gutters class="font-weight-medium"> {{ $i18n.t('sidebar.searchArea.title3') }} </v-row>
                      <v-row no-gutters class="italic--text"> {{ $i18n.t('sidebar.searchArea.tooltip3') }} </v-row>
                    </v-col>
                  </v-row>
                </popover-menu>
              </v-row>
            </v-col>
          </v-row>
          <v-row no-gutters class="py-2">
            <v-col cols="auto"> {{ $i18n.t('sidebar.searchArea.which') }}: </v-col>
          </v-row>
          <v-row no-gutters class="py-2 tw:gap-1.5" align="center">
            <v-col class="tw:min-w-0">
              <data-input
                v-model="newPayloadParam.criteria"
                v-bind="newCriteriaInput"
                :menu-props="{
                  contentClass: 'tw:[&_.v-list-item--disabled]:hidden',
                }"
              />
            </v-col>
            <v-col cols="5" class="pr-2">
              <data-input
                class="oze-area-search-select red--text"
                v-model="newPayloadParam.value"
                v-bind="paramInput"
                :style="[getColor(newPayloadParam.value)]"
              />
            </v-col>
            <v-col cols="auto">
              <v-icon color="success" :disabled="!isNewParamValid" @click="addParam">mdi-check</v-icon>
            </v-col>
          </v-row>
          <v-row
            no-gutters
            class="py-2 tw:gap-1.5"
            v-for="({ name, mode }, idx) of payload.params"
            :key="idx"
            align="center"
          >
            <v-col class="tw:min-w-0">
              <data-input
                :value="name"
                @change="$event => onParamChanged($event, idx)"
                v-bind="criteriaInput"
                :menu-props="{
                  contentClass: 'tw:[&_.v-list-item--disabled]:hidden',
                }"
              />
            </v-col>
            <v-col cols="5" class="pr-2">
              <data-input
                class="oze-area-search-select red--text"
                :value="mode"
                @change="$event => (payload.params[idx].mode = $event)"
                v-bind="paramInput"
                :style="[getColor(payload.params[idx].mode)]"
              />
            </v-col>
            <v-col cols="auto">
              <v-icon color="error" @click="deleteParam(idx)">mdi-close</v-icon>
            </v-col>
          </v-row>
          <v-row no-gutters class="py-2">
            <v-col cols="auto"> {{ $i18n.t('sidebar.searchArea.optionalExcludedLayersTitle') }}: </v-col>
          </v-row>
          <v-row no-gutters class="py-2">
            <v-col cols="12">
              <data-input v-model="payload.excluded_layers" v-bind="excludedLayersInput">
                <template #prepend-item>
                  <v-list-item @click="toggleSelectAllExcludedLayers">
                    <v-list-item-action>
                      <v-icon :color="payload.excluded_layers.length > 0 ? 'primary' : ''">
                        {{ getIconSelectAllExcludedLayers() }}
                      </v-icon>
                    </v-list-item-action>
                    <v-list-item-content>
                      <v-list-item-title> {{ $i18n.t('button.selectAllItems') }} </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-divider />
                </template>
                <template #selection="{ item, index }">
                  <v-tooltip color="font" bottom>
                    <template #activator="{ on }">
                      <v-chip small v-if="index < 2" v-on="on">
                        <span class="d-inline-block text-truncate">{{ item.text }}</span>
                      </v-chip>
                    </template>
                    {{ item.text }}
                  </v-tooltip>
                  <span v-if="index === 2" style="white-space: nowrap" class="text-caption grey--text">
                    {{ `(+${$i18n.tc('admin.other', payload.excluded_layers.length - 2)})` }}
                  </span>
                </template>
              </data-input>
            </v-col>
          </v-row>
          <v-row v-if="selectedFeatures?.length > 1" no-gutters class="py-2">
            <data-input v-model="payload.is_intersection" v-bind="checkboxInput">
              <template #label>
                <span class="font--text">{{ $i18n.t('sidebar.searchArea.is_intersection') }}</span>
              </template>
            </data-input>
          </v-row>
          <v-row no-gutters class="py-2">
            <data-input v-model="payload.is_within" v-bind="checkboxInput">
              <template #label>
                <span class="font--text">{{ $i18n.t('sidebar.searchArea.is_within') }}</span>
              </template>
            </data-input>
          </v-row>
        </span>
        <oze-project-copy-dialog
          v-if="isCopyOzeVisible !== null"
          :is-visible.sync="isCopyOzeVisible"
          :results="results"
          :results-attributes="resultAttributes"
        />
      </div>
    </template>
    <template #[`content.footer`]>
      <base-button
        translation-path="dialog.agreeSearch"
        class="ml-3"
        :disabled="!isFormValid"
        :loading="isLoading"
        @click="search"
      />
      <base-button
        class="mx-0"
        v-if="results"
        @click="reset"
        :disabled="isLoading"
        translation-path="default.clearResult"
        cancel
      />
      <v-col v-if="!!results" cols="auto">
        <save-geometry-trigger
          :tooltip="$i18n.t('sidebar.searchArea.saveResult')"
          :filename="$i18n.t('sidebar.searchArea.areas')"
          :geojson="results"
          :additional-actions="{
            ozeProject: {
              customName: $i18n.t('sidebar.searchArea.addToOzeProject'),
              action: () => (isCopyOzeVisible = true),
            },
          }"
          :copy-dialog-props="{
            name: 'investment-area-search',
            inputLayerId: 'investment-area-search',
            geojsonCopyAttributes: resultAttributes,
          }"
        />
      </v-col>
    </template>
  </navbar-menu-tool>
</template>
<script>
import { call, get } from 'vuex-pathify';

import OzeProjectCopyDialog from '@/components/OzeProjectCopyDialog';
import DataInput from '@/components/DataInput';
import DotsMenu from '@/components/DotsMenu';
import NavbarMenuTool from '@/components/NavbarMenuTool';
import SaveGeometryTrigger from '@/components/SaveGeometryTrigger';

import PopoverMenu from '@/components/PopoverMenu';

import { createBox } from 'ol/interaction/Draw';
import { Fill, Stroke, Style, Text } from 'ol/style';

import { debounce } from 'lodash';

const INVEST_PARAMS = {
  area_from: null,
  area_to: null,
  excluded_layers: [],
  geojson: null,
  is_intersection: undefined,
  is_within: undefined,
  layers: undefined,
  params: [],
};

export default {
  name: 'TheNavbarToolsPanelOzeAreaSearch',
  components: {
    DataInput,
    DotsMenu,
    NavbarMenuTool,
    OzeProjectCopyDialog,
    PopoverMenu,
    SaveGeometryTrigger,
  },
  props: {
    icon: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    tooltip: {
      type: String,
    },
  },
  computed: {
    moduleShareableData: get('moduleSharing/moduleSharingData'),
    selectedFeaturesLayers: get('map/selectedFeatures'),
    areaInput() {
      return {
        dataType: { name: 'decimal', decimal_places: 3 },
        disabled: this.loading,
        hideDetails: true,
      };
    },
    buttonStyle() {
      return this.isDrawing
        ? {
            backgroundColor: `${this.$_colors.primarybackground} !important`,
            border: '1px solid rgba(26, 115, 232, 0.3) !important',
            color: this.$_colors.primary,
          }
        : {
            backgroundColor: 'rgba(0, 0, 0, 0.04) !important',
            border: '1px solid rgba(0, 0, 0, 0.2) !important',
            color: '#61646D',
          };
    },
    checkboxInput() {
      return {
        dataType: { name: 'checkbox' },
        disabled: this.loading,
        hideDetails: true,
      };
    },
    excludedLayersInput() {
      return {
        dataType: { name: 'select' },
        items: this.projectLayersMapped,
        chips: true,
        multiple: true,
        autoConvertSelect: false,
        disabled: this.loading,
        hideDetails: true,
      };
    },
    defaultCriteriaInput() {
      return {
        label: this.$i18n.t('sidebar.searchArea.layer'),
        hideDetails: true,
        disabled: this.loading,
        disabledSorting: true,
        dataType: { name: 'select' },
      };
    },
    newCriteriaInput() {
      return {
        ...this.defaultCriteriaInput,
        items: Object.keys(this.paramsDict)
          .filter(param => !this.payload.params.find(p => p.name === param))
          .map(param => ({
            ...this.paramsDict[param],
            value: param,
          })),
      };
    },
    criteriaInput() {
      return {
        ...this.defaultCriteriaInput,
        items: Object.keys(this.paramsDict).map(param => ({
          ...this.paramsDict[param],
          value: param,
          disabled: Object.keys(this.payload.params)
            .filter(param => this.payload.params[param] !== null)
            .includes(param),
        })),
      };
    },
    paramInput() {
      return {
        dataType: { name: 'select' },
        items: [
          { value: 'include', text: this.$i18n.t('sidebar.searchArea.included') },
          { value: 'exclude', text: this.$i18n.t('sidebar.searchArea.excluded') },
        ],
        hideDetails: true,
        disabled: this.loading,
        disabledSorting: true,
      };
    },
    isNewParamValid() {
      return this.newPayloadParam.criteria && this.newPayloadParam.value;
    },

    layers: get('layers/layers'),
    currentProjectId: get('layers/project@id'),
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers.filter(
        layer => layer.geometry_type === 'polygon' || layer.geometry_type === 'multipolygon'
      );
    },
    projectLayersMapped() {
      return this.projectLayers.map(layer => ({ value: layer.id, text: layer.name }));
    },

    ozeModule: get('admin/additionalModules@OZE_MODULE'),
    ozeModulePermissions: get('users/toolsPermissions@OZE_MODULE.main_value'),
    ozeSettingEnabled: get('admin/settingsValues@oze_module_enabled'),
    ozeParcelsDatasource: get('admin/modulesMapping@oze.oze_parcels_datasource.datasource'),
    ozeProjectsDatasource: get('admin/modulesMapping@oze.oze_projects_datasource.datasource'),
    isOzeModuleConfigured() {
      return Boolean(
        this.ozeModule?.enabled &&
          this.ozeModule?.configured &&
          this.ozeModulePermissions > 0 &&
          this.ozeSettingEnabled &&
          this.ozeParcelsDatasource &&
          this.ozeProjectsDatasource
      );
    },
  },
  data: vm => ({
    menuOpenWatch: undefined,
    dotsItemsArea: [
      { name: 'multiple' },
      { name: 'freehand' },
      { name: 'single', customName: vm.$i18n.t('menu.selectFeatures') },
    ],
    isCopyOzeVisible: null,
    isDrawing: false,
    isDrawn: false,
    isFormValid: false,
    isMenuOpen: false,
    isLoading: false,
    loading: false,
    initLoading: false,
    paramsDict: [],
    payload: INVEST_PARAMS,
    newPayloadParam: {
      criteria: null,
      value: 'include',
    },

    results: undefined,
    selectedFeatures: [],
    resultAttributes: [],
  }),
  methods: {
    getSearchedArea: call('externalSystems/getSearchedArea'),
    getDataBoxLayersMetadata: call('externalSystems/getDataBoxLayersMetadata'),
    getDbGisBoxMetadata: call('externalSystems/getDbGisBoxMetadata'),
    getButtonListeners(on) {
      return this.isDrawing ? { click: this.turnOffSelecting } : on;
    },
    turnOffSelecting() {
      if (this.selectedFeatures.length) this.isDrawn = true;
      this.isDrawing = false;
      this.$root.$emit('selection-action');
    },
    turnOnDrawing(type) {
      this.isDrawing = true;
      if (type === 'single') {
        this.$root.$emit('identification-action', true, { specialType: 'addByMap' });
        this.$root.$off('assignedObjects');
        this.$root.$on('assignedObjects', identifiedFeatures => {
          const features = identifiedFeatures.map(feature => feature.features);
          for (const feature of features.flat()) {
            const { dataSource = '', layerId, id: featureId } = feature;
            if (!dataSource) return;
            const isFeatureSelected = this.selectedFeatures.some(el => el.layerId === layerId && el.id === featureId);
            if (isFeatureSelected) this.deleteFeature(feature);
            else {
              this.selectedFeatures.push(feature);
              this.$store.set('map/ADD_SELECTED_FEATURES!', {
                type: 'selectedFeatures',
                features: { [layerId]: [featureId] },
              });
              this.$root.$emit('setProjectLayerStyle', layerId);
            }
          }
        });
      } else {
        this.$root.$emit('drawSidebarGeometry', {
          drawendCallback: e => this.onDrawend(e, type),
          geometryFunction: type === 'multiple' ? createBox() : null,
          geometryType: 'polygon',
          isInitSnapping: false,
          freehand: type === 'freehand' ? true : false,
          ...(type === 'multiple' ? { drawType: 'Circle' } : {}),
        });
      }
    },
    clearNewCriteria(param) {
      if (param === this.newPayloadParam.criteria)
        this.newPayloadParam = {
          ...this.newPayloadParam,
          criteria: null,
        };
    },
    addParam() {
      this.payload = {
        ...this.payload,
        params: [
          {
            mode: this.newPayloadParam.value,
            name: this.newPayloadParam.criteria,
          },
          ...this.payload.params,
        ],
      };
      this.clearNewCriteria(this.newPayloadParam.criteria);
    },
    deleteParam(id) {
      let newPayload = { ...this.payload };
      newPayload.params = newPayload.params.filter((_, idx) => idx !== id);
      this.payload = { ...newPayload };
    },
    onParamChanged(newParam, index) {
      let newPayload = { ...this.payload };
      newPayload.params[index].name = newParam;
      this.clearNewCriteria(newParam);
      this.payload = { ...newPayload };
    },
    onDrawend(e, type) {
      this.isDrawing = false;
      this.isDrawn = true;
      if (['freehand', 'multiple'].includes(type)) {
        this.payload.geojson = {
          type: 'Polygon',
          crs: {
            type: 'name',
            properties: { name: this.$_config.defaultEpsg || 'EPSG:4326' },
          },
          coordinates: e.feature.getGeometry().getCoordinates(),
        };
      }
    },
    clearSelection() {
      this.isDrawing = false;
      this.isDrawn = false;
      this.payload.geojson = null;
      if (this.selectedFeatures.length) this.deleteFeatures();
      this.$root.$emit('clearSidebarGeometry');
      this.$root.$emit('identification-action', false);
    },
    deleteFeature({ layerId, id: featureId }) {
      if (featureId && layerId) {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'selectedFeatures',
          features: {
            [layerId]: [featureId],
          },
        });
      }
      const featureIndex = this.selectedFeatures.findIndex(ft => ft.id === featureId && ft.layerId === layerId);
      this.selectedFeatures.splice(featureIndex, 1);
    },
    deleteFeatures() {
      for (const layerId of Object.keys(this.selectedFeaturesLayers)) {
        this.$store.set('map/DELETE_SELECTED_FEATURES!', {
          type: 'selectedFeatures',
          features: {
            [layerId]: [],
          },
        });
      }
      this.selectedFeatures = [];
    },

    getColor(value) {
      if (value === 'include')
        return {
          '--current-color': this.$_colors['success'],
          '--current-color-light': this.$_getHexColorLighter(this.$_colors['success'], 90),
        };
      else if (value === 'exclude')
        return {
          '--current-color': this.$_colors['error'],
          '--current-color-light': this.$_getHexColorLighter(this.$_colors['error'], 90),
        };
      return {
        '--current-color': this.$_colors['italic'],
        '--current-color-light': this.$_getHexColorLighter(this.$_colors['italic'], 90),
      };
    },
    getIconSelectAllExcludedLayers() {
      if (this.payload.excluded_layers.length === this.excludedLayersInput.items.length) return 'mdi-checkbox-marked';
      else if (this.payload.excluded_layers.length) return 'mdi-minus-box';
      else return 'mdi-checkbox-blank-outline';
    },
    toggleSelectAllExcludedLayers() {
      if (this.payload.excluded_layers.length === this.excludedLayersInput.items.length)
        this.payload.excluded_layers = [];
      else this.payload.excluded_layers = this.excludedLayersInput.items.map(item => item.value);
    },

    setLocalStorage(value = this.payload) {
      localStorage.setItem(
        'oze-area-search-params',
        JSON.stringify(this.$_filterObjectKeys(value, { notAllowedKeys: ['geojson', 'layers'] }))
      );
    },
    setInitParams() {
      const paramsFromLocalStorage = Object.entries(
        JSON.parse(localStorage.getItem('oze-area-search-params') || '{}')
      ).reduce(
        (acc, [key, value]) => ({ ...acc, ...(Object.keys(INVEST_PARAMS).includes(key) && { [key]: value }) }),
        {}
      );
      paramsFromLocalStorage.excluded_layers = (paramsFromLocalStorage.excluded_layers || []).filter(layerId => {
        return this.projectLayersMapped.some(layer => layer.value === layerId);
      });
      this.payload = {
        ...this.payload,
        ...paramsFromLocalStorage,
        params: [
          ...this.payload.params,
          ...(Array.isArray(paramsFromLocalStorage.params)
            ? paramsFromLocalStorage.params.filter(param => !!this.paramsDict[param.name])
            : []),
        ],
      };
    },
    reset() {
      this.$root.$emit('toggleCustomVectorLayer', { id: 'searchArea' });
      this.payload = { ...INVEST_PARAMS };
      this.setInitParams();
      this.results = undefined;
    },
    async search() {
      this.isLoading = true;
      try {
        let payload = JSON.parse(JSON.stringify(this.payload));
        if (this.selectedFeatures.length) {
          payload.layers = [];
          this.selectedFeatures.forEach(feature => {
            if (payload.layers.some(layer => layer.layer_id === feature.layerId))
              payload.layers.find(layer => layer.layer_id === feature.layerId).ids.push(feature.id);
            else payload.layers.push({ layer_id: feature.layerId, ids: [feature.id] });
          });
        } else payload.is_intersection = false;
        let results = await this.getSearchedArea(payload);
        this.results = results;
        const geojson = { ...this.results };
        if (geojson?.features?.length)
          this.$root.$emit('toggleCustomVectorLayer', {
            id: 'searchArea',
            geometry: geojson,
            fitView: true,
            layerParams: {
              style: f => {
                const areaM2 = f.get('area_m2');
                const areaHa = areaM2 ? (areaM2 / 10000).toFixed(2) : 0;
                return [
                  new Style({
                    fill: new Fill({
                      color: 'rgba(200, 0, 0, 0.6)',
                    }),
                    stroke: new Stroke({
                      color: 'rgba(255, 0, 0, 1)',
                      width: 2,
                    }),
                    text: new Text({
                      text: `${areaHa} ha`,
                      font: 'normal 14px sans-serif',
                      fill: new Fill({ color: 'rgba(191, 54, 12, 1)' }),
                      stroke: new Stroke({
                        color: '#FFFFFF',
                        opacity: 1,
                        width: 3,
                        dash: [],
                      }),
                      overflow: true,
                    }),
                  }),
                ];
              },
            },
          });
        this.clearSelection();
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },

    async init() {
      this.initLoading = true;
      const [databoxLayers, resultAttributes] = await Promise.all([
        this.getDbGisBoxMetadata({ tag: 'invest' }),
        this.getDataBoxLayersMetadata('parcels'),
      ]);
      this.paramsDict = databoxLayers.reduce(
        (acc, { title, name }) => ({
          ...acc,
          [name]: {
            text: title,
          },
        }),
        {}
      );
      this.resultAttributes = resultAttributes.columns.map(attribute => {
        return {
          attribute: attribute.name,
          dataType: { name: attribute.type },
          label: attribute.verbose_name,
        };
      });
      this.menuOpenWatch();
      this.setInitParams();
      this.initLoading = false;
    },
  },
  watch: {
    'payload.geojson': {
      handler(nV) {
        this.isFormValid = !!nV;
      },
    },
    payload: {
      handler(nV) {
        this.setLocalStorage(nV);
      },
      deep: true,
    },
    selectedFeatures(nV) {
      this.isFormValid = !!nV.length;
    },
    currentProjectId: {
      handler(nV, oV) {
        if (nV !== oV)
          this.payload.excluded_layers = this.payload.excluded_layers.filter(layerId => {
            return this.projectLayersMapped.some(layer => layer.value === layerId);
          });
      },
    },
    isMenuOpen(nV) {
      if (!nV) {
        this.isLoading = false;
        this.clearSelection();
      }
    },
    moduleShareableData: {
      handler(nV) {
        if (nV?.destinationTool === 'searchArea') {
          this.payload.geojson = JSON.parse(this.moduleShareableData.geometry);
          this.isDrawn = true;
          this.$store.set('moduleSharing/CLEAR_SHAREABLE_DATA!');
          this.isMenuOpen = true;
        }
      },

      deep: true,
    },
  },
  mounted() {
    this.setLocalStorage = debounce(this.setLocalStorage, 3000);
    this.menuOpenWatch = this.$watch(
      vm => vm.isMenuOpen,
      nV => {
        if (nV) this.init();
      },
      { immediate: true }
    );
  },
};
</script>
<style lang="scss" scoped>
::v-deep {
  .input-area .v-input__control {
    width: 75px;
  }
  .oze-area-search-select {
    .v-input__slot {
      background-color: var(--current-color-light) !important;
      fieldset {
        border: unset !important;
      }
    }
    .v-select__selection {
      color: var(--current-color) !important;
      font-weight: 500 !important;
    }
    .v-select__selections input::placeholder {
      color: var(--current-color) !important;
    }
    .v-input__icon i {
      color: var(--current-color) !important;
      font-size: 20px;
    }
  }
}
</style>
